import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { pushToDataLayer } from 'components/helpers/Global/Analytics/DataLayer';

import { BaseActionButtonProps } from '../BaseActionButtonProps';

import commonStyles from '../ActionButtonCommon.module.scss';
import styles from './SaveButton.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { useFavorite } from 'lib/hooks/useFavorite';
import useDictionary from 'lib/hooks/useDictionary';
import { ActionToolbarVariant } from '../../ActionToolbar';
import { BaseContentResponse } from 'lib/services/models/content-response';
import { useContentItem } from 'lib/hooks/useContentItem';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';

interface SaveButtonProps extends Omit<BaseActionButtonProps, 'variant' | 'label'> {
  variant: ActionToolbarVariant | 'card';
  /** If not provided, will use the contentItem from the Sitecore Context */
  contentItem?: BaseContentResponse;
  disabled?: boolean;
  isSubAction?: boolean;
}

// Module-level flag to ensure GTM event is only sent once across all instances
let gtmEventSent = false;

export function SaveButton({
  variant,
  disabled,
  contentItem,
  isSubAction = false,
  toolbarIdentifier,
  eventPositionSuffix,
}: SaveButtonProps): JSX.Element {
  const contentItemFallback = useContentItem();
  const content = contentItem ?? contentItemFallback;
  const contentId = content?.ContentId ?? '';
  const contentTitle = content?.Title ?? '';
  const contentType = content?.ContentType ?? '';
  const recordType = content?.RecordType ?? '';
  const { siteName: siteId } = useGlobalData();
  const { getDictionaryValue } = useDictionary();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const shouldRestoreFocusRef = useRef(false);

  const getGtmPosition = useCallback((): string => {
    return [toolbarIdentifier, eventPositionSuffix].filter(Boolean).join('.') || '';
  }, [toolbarIdentifier, eventPositionSuffix]);

  const gtmEvent = useMemo(
    () => ({
      content_id: contentId,
      content_title: contentTitle,
      content_type: contentType,
      record_type: recordType,
      event: 'add_to_favorites',
      position: getGtmPosition(),
      ...(variant === 'card' && {
        content_list_name: 'RelatedContent', // TODO: Change hard coded name in future
        template_name: 'RelatedContent', // TODO: Change hard coded name in future
      }),
    }),
    [contentId, contentTitle, contentType, recordType, getGtmPosition, variant]
  );

  const { isFavorite, isProcessing, toggleFavorite, status, hasProcessedAddFavorite } = useFavorite(
    {
      contentId,
      title: contentTitle,
      contentType,
      siteId,
    }
  );

  useEffect(() => {
    if (hasProcessedAddFavorite && gtmEvent && !gtmEventSent) {
      pushToDataLayer({ ...gtmEvent });
      gtmEventSent = true;
    }
  }, [hasProcessedAddFavorite, gtmEvent]);

  // Restore focus after isFavorite changes (when toggleFavorite completes)
  useEffect(() => {
    if (shouldRestoreFocusRef.current && buttonRef.current && !isProcessing) {
      buttonRef.current?.focus();
      shouldRestoreFocusRef.current = false;
    }
  }, [isFavorite, isProcessing]);

  const handleClick = () => {
    if (!disabled && !isProcessing) {
      // Check if button currently has focus (e.g., from keyboard Enter key)
      const hadFocus = document.activeElement === buttonRef.current;
      shouldRestoreFocusRef.current = hadFocus;
      toggleFavorite();
    }
  };

  const ctaStyle =
    variant === 'horizontal' && !isSubAction
      ? 'filled'
      : variant === 'card'
        ? 'link-invert-underline'
        : 'none';

  const label = isFavorite
    ? getDictionaryValue('Components.ActionToolBar.SaveButtonLabel_Active')
    : getDictionaryValue('Components.ActionToolBar.SaveButtonLabel');

  const screenReaderText = isFavorite
    ? getDictionaryValue('Components.ActionToolBar.SaveButtonLabelScreenReaderText_Active')
    : getDictionaryValue('Components.ActionToolBar.SaveButtonLabelScreenReaderText');

  return (
    <CtaButton
      ref={buttonRef}
      ctaStyle={ctaStyle}
      {...(variant === 'card'
        ? { ctaComponentClass: 'theme-primary-bg-cta-1-color' as const }
        : { ctaComponentClass: 'componentTheme-cards-match-bg-cta-1-color' as const })}
      type="button"
      className={clsx(
        commonStyles.actionButtonBase,
        commonStyles[`actionButton--${variant}`],
        isSubAction && commonStyles['actionButtonSubAction']
      )}
      onClick={handleClick}
      aria-label={screenReaderText
        .replace('{contentType}', contentType)
        .replace('{title}', contentTitle)}
      ctaIconBefore={isFavorite ? 'heart-filled' : 'heart'}
      ctaIconSize="sm"
      ctaIconNoSpacer={true}
      ctaIconClassName={styles.icon}
      disabled={disabled || isProcessing}
      gtmEvent={!isFavorite && status === 'authenticated' ? gtmEvent : undefined}
      text={label}
    />
  );
}
